import { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Printer, X, Receipt, Mail, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Default company info (used as fallback ONLY when /settings/company fails — blank to avoid misleading addresses).
const DEFAULT_COMPANY_INFO = {
  name: "BBH - Bosveld Bou & Hardeware",
  address: "",
  city: "",
  phone: "",
  email: "",
  vatNumber: "",
  logo: "/logo.png"
};

export const AccountPaymentReceiptModal = ({ open, onClose, payment }) => {
  const [receiptFormat, setReceiptFormat] = useState("thermal");
  const [companyInfo, setCompanyInfo] = useState(DEFAULT_COMPANY_INFO);
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [emailAddress, setEmailAddress] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const printRef = useRef();

  // Fetch company settings when modal opens
  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const response = await axios.get(`${API}/settings/company`);
        const settings = response.data;
        setCompanyInfo({
          name: settings.company_name || DEFAULT_COMPANY_INFO.name,
          address: settings.address_line1 || DEFAULT_COMPANY_INFO.address,
          city: [settings.city, settings.province, settings.postal_code].filter(Boolean).join(", ") || DEFAULT_COMPANY_INFO.city,
          phone: settings.phone || DEFAULT_COMPANY_INFO.phone,
          storeNumber: settings.fax || "",
          email: settings.email || DEFAULT_COMPANY_INFO.email,
          vatNumber: settings.vat_number ? `VAT No: ${settings.vat_number}` : DEFAULT_COMPANY_INFO.vatNumber,
          logo: settings.logo_url || DEFAULT_COMPANY_INFO.logo,
          footerLine1: settings.receipt_footer_line1 || "Thank you for your business!",
          footerLine2: settings.receipt_footer_line2 || "",
          footerLine3: settings.receipt_footer_line3 || ""
        });
      } catch (error) {
        console.error("Error fetching company settings:", error);
      }
    };

    if (open) {
      fetchCompanySettings();
      // Pre-fill email if customer has email
      if (payment?.customer_email) {
        setEmailAddress(payment.customer_email);
      }
    }
  }, [open, payment]);

  const handleSendEmail = async () => {
    if (!emailAddress || !emailAddress.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    setSendingEmail(true);
    try {
      await axios.post(`${API}/email/account-payment`, {
        payment_id: payment.id,
        recipient_email: emailAddress
      });
      toast.success(`Receipt sent to ${emailAddress}`);
      setShowEmailInput(false);
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank', 'width=302,height=600');
    
    if (!printWindow) {
      toast.error("Pop-up blocked. Please allow pop-ups for printing.");
      return;
    }

    const styles = receiptFormat === "thermal" ? `
      <style>
        @page { 
          size: 72mm auto; 
          margin: 0mm;
        }
        * { 
          box-sizing: border-box; 
          margin: 0; 
          padding: 0;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        body { 
          font-family: 'Courier New', 'Lucida Console', monospace; 
          font-size: 14px; 
          font-weight: 900 !important;
          line-height: 1.4;
          width: 70mm; 
          margin: 0 auto; 
          padding: 1mm;
          color: #000 !important;
          text-transform: uppercase;
          -webkit-font-smoothing: none;
        }
        .receipt { width: 100%; font-weight: 900 !important; }
        .text-center { text-align: center; }
        .text-right { text-align: right; }
        div, span, p { font-weight: 900 !important; color: #000 !important; }
        img { 
          width: 100% !important; 
          max-width: 68mm !important; 
          min-height: 20mm;
          height: auto !important; 
          display: block !important; 
          margin: 0 auto 6mm auto !important; 
        }
        @media print { 
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          html, body {
            width: 72mm;
            margin: 0;
            padding: 2mm;
          }
        }
      </style>
    ` : `
      <style>
        @page { size: A4; margin: 15mm; }
        body { 
          font-family: Arial, sans-serif; 
          font-size: 12px; 
          margin: 0; 
          padding: 20px;
        }
        .receipt { max-width: 800px; margin: 0 auto; }
        table { border-collapse: collapse; width: 100%; }
        th, td { padding: 8px; text-align: left; }
        .text-right { text-align: right; }
        .font-bold { font-weight: bold; }
        @media print {
          body { margin: 0; }
        }
      </style>
    `;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Account Payment Receipt ${payment?.receipt_number}</title>
          ${styles}
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  if (!payment) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return new Date().toLocaleString('en-ZA');
    const date = new Date(dateStr);
    return date.toLocaleString('en-ZA', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-green-600" />
              Account Payment Receipt
            </span>
            <div className="flex gap-2">
              <Button
                variant={receiptFormat === "thermal" ? "default" : "outline"}
                size="sm"
                onClick={() => setReceiptFormat("thermal")}
              >
                80mm Thermal
              </Button>
              <Button
                variant={receiptFormat === "a4" ? "default" : "outline"}
                size="sm"
                onClick={() => setReceiptFormat("a4")}
              >
                A4 Receipt
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Print Buttons */}
        <div className="flex justify-end gap-2 mb-4">
          {showEmailInput ? (
            <div className="flex items-center gap-2 bg-gray-50 p-2 rounded-lg">
              <Input
                type="email"
                placeholder="customer@email.com"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                className="w-48"
              />
              <Button 
                onClick={handleSendEmail} 
                disabled={sendingEmail}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {sendingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send"}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setShowEmailInput(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button 
              variant="outline" 
              onClick={() => setShowEmailInput(true)}
              className="border-blue-300 text-blue-600 hover:bg-blue-50"
            >
              <Mail className="h-4 w-4 mr-2" />
              Email Receipt
            </Button>
          )}
          <Button onClick={handlePrint} className="bg-green-600 hover:bg-green-700">
            <Printer className="h-4 w-4 mr-2" />
            Print Receipt
          </Button>
          <Button variant="outline" onClick={onClose}>
            <X className="h-4 w-4 mr-2" />
            Close
          </Button>
        </div>

        {/* Receipt Preview */}
        <div className={`border rounded-lg p-4 ${receiptFormat === "thermal" ? "bg-white max-w-[250px] mx-auto" : "bg-white"}`}>
          <div ref={printRef}>
            {receiptFormat === "thermal" ? (
              <ThermalReceipt payment={payment} formatDate={formatDate} companyInfo={companyInfo} />
            ) : (
              <A4Receipt payment={payment} formatDate={formatDate} companyInfo={companyInfo} />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// 72mm Thermal Receipt Component - Matching main sales receipt style
const ThermalReceipt = ({ payment, formatDate, companyInfo }) => {
  return (
    <div className="receipt font-mono uppercase" style={{ width: '70mm', fontWeight: 'bold', color: '#000' }}>
      {/* Logo - FULL WIDTH */}
      <div className="text-center" style={{ marginBottom: '6mm' }}>
        <img 
          src={companyInfo.logo} 
          alt={companyInfo.name}
          style={{ width: '100%', maxWidth: '68mm', height: 'auto', minHeight: '20mm', objectFit: 'contain', display: 'block', margin: '0 auto' }}
        />
      </div>

      {/* Company Header - Larger text */}
      <div className="text-center" style={{ marginBottom: '5mm', fontSize: '16px', fontWeight: 'bold' }}>
        <div style={{ fontSize: '18px', fontWeight: '900' }}>{companyInfo.name}</div>
        <div>{companyInfo.address}</div>
        <div>{companyInfo.city}</div>
        <div>TEL: {companyInfo.phone}</div>
        {companyInfo.storeNumber && <div>STORE: {companyInfo.storeNumber}</div>}
      </div>

      {/* Spacer */}
      <div style={{ height: '5mm' }}></div>

      {/* Receipt Number - Large and bold */}
      <div className="text-center" style={{ fontSize: '18px', fontWeight: '900', marginBottom: '3mm' }}>
        RECEIPT NO: {payment.receipt_number?.replace(/[A-Z]+-/, '') || payment.receipt_number}
      </div>

      {/* Spacer */}
      <div style={{ height: '4mm' }}></div>

      {/* Transaction Info */}
      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
        <div>Date: {formatDate(payment.created_at)}</div>
        <div>Cashier: {payment.cashier || "Cashier 1"}</div>
      </div>

      {/* Customer Details Section */}
      <div style={{ height: '4mm' }}></div>
      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
        <div style={{ fontWeight: '900' }}>CUSTOMER:</div>
        <div>{payment.customer_name}</div>
        {payment.customer_account_number && (
          <div>A/C: {payment.customer_account_number}</div>
        )}
      </div>

      {/* Spacer */}
      <div style={{ height: '5mm' }}></div>

      {/* Payment Details */}
      <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
        <div style={{ marginBottom: '3mm' }}>
          <div style={{ fontWeight: '900' }}>ACCOUNT PAYMENT</div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Balance Before:</span>
            <span>R {payment.balance_before?.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Spacer */}
      <div style={{ height: '5mm' }}></div>

      {/* Payment Amount - Extra bold and large */}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '20px', fontWeight: '900' }}>
        <span>Payment:</span>
        <span>R {payment.amount?.toFixed(2)}</span>
      </div>

      {/* Spacer */}
      <div style={{ height: '4mm' }}></div>

      {/* Payment Method & New Balance */}
      <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>{payment.payment_method}:</span>
          <span>{payment.amount?.toFixed(2)}</span>
        </div>
        <div style={{ height: '3mm' }}></div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '900' }}>
          <span>New Balance:</span>
          <span>{payment.balance_after?.toFixed(2)}</span>
        </div>
      </div>

      {/* Note */}
      {payment.note && (
        <>
          <div style={{ height: '4mm' }}></div>
          <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
            <div style={{ fontWeight: '900' }}>NOTE:</div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{payment.note}</div>
          </div>
        </>
      )}

      {/* Spacer */}
      <div style={{ height: '5mm' }}></div>

      {/* Footer */}
      <div className="text-center" style={{ fontSize: '14px', fontWeight: '900' }}>
        <div>THANK YOU FOR YOUR PAYMENT!</div>
        {companyInfo.footerLine1 && <div style={{ marginTop: '2mm' }}>{companyInfo.footerLine1}</div>}
        {companyInfo.footerLine2 && <div>{companyInfo.footerLine2}</div>}
        {companyInfo.footerLine3 && <div style={{ marginTop: '2mm' }}>{companyInfo.footerLine3}</div>}
      </div>
    </div>
  );
};

// A4 Receipt Component
const A4Receipt = ({ payment, formatDate, companyInfo }) => {
  return (
    <div className="receipt" style={{ minHeight: '500px' }}>
      {/* Header with Logo */}
      <div className="flex justify-between mb-8 pb-4 border-b-2 border-gray-300">
        <div className="company-info">
          <img 
            src={companyInfo.logo} 
            alt={companyInfo.name}
            style={{ maxWidth: '200px', maxHeight: '80px', objectFit: 'contain' }}
            className="mb-2"
          />
          <p className="text-gray-600">{companyInfo.address}</p>
          <p className="text-gray-600">{companyInfo.city}</p>
          <p className="text-gray-600">Tel: {companyInfo.phone}</p>
          {companyInfo.storeNumber && (
            <p className="text-gray-600">Store: {companyInfo.storeNumber}</p>
          )}
          <p className="text-gray-600">{companyInfo.vatNumber}</p>
          <p className="text-gray-600">{companyInfo.email}</p>
        </div>
        <div className="text-right">
          <h2 className="text-3xl font-bold text-green-600">PAYMENT RECEIPT</h2>
          <p className="text-lg font-semibold mt-2">{payment.receipt_number}</p>
          <p className="text-gray-600 mt-4">Date: {formatDate(payment.created_at)}</p>
          <p className="text-gray-600">Cashier: {payment.cashier}</p>
        </div>
      </div>

      {/* Customer Info */}
      <div className="mb-6 p-4 bg-gray-50 rounded border border-gray-200">
        <h3 className="font-bold text-gray-700 mb-2 text-lg">Customer Details:</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="font-semibold text-lg">{payment.customer_name}</p>
            {payment.customer_account_number && (
              <p className="text-gray-600">Account No: {payment.customer_account_number}</p>
            )}
          </div>
        </div>
      </div>

      {/* Payment Details */}
      <div className="mb-6">
        <h3 className="font-bold text-gray-700 mb-4 text-lg">Payment Details:</h3>
        <table className="w-full">
          <tbody>
            <tr className="border-b">
              <td className="p-3 text-gray-600">Account Balance Before Payment:</td>
              <td className="p-3 text-right font-semibold">R {payment.balance_before?.toFixed(2)}</td>
            </tr>
            <tr className="border-b bg-green-50">
              <td className="p-3 font-bold text-green-700">Payment Amount:</td>
              <td className="p-3 text-right font-bold text-green-700 text-xl">R {payment.amount?.toFixed(2)}</td>
            </tr>
            <tr className="border-b">
              <td className="p-3 text-gray-600">Payment Method:</td>
              <td className="p-3 text-right font-semibold">{payment.payment_method}</td>
            </tr>
            <tr className="bg-gray-100">
              <td className="p-3 font-bold text-lg">New Account Balance:</td>
              <td className={`p-3 text-right font-bold text-xl ${payment.balance_after <= 0 ? 'text-green-600' : 'text-orange-600'}`}>
                R {payment.balance_after?.toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Note */}
      {payment.note && (
        <div className="mb-6 p-4 bg-gray-50 rounded">
          <h3 className="font-bold text-gray-700 mb-2">Note:</h3>
          <p className="text-gray-600">{payment.note}</p>
        </div>
      )}

      {/* Footer */}
      <div className="mt-12 pt-4 border-t text-center text-gray-500 text-sm">
        <p className="font-bold text-lg text-green-600 mb-2">Thank you for your payment!</p>
        <p className="mt-4">
          {[companyInfo.name, companyInfo.phone, companyInfo.storeNumber && `Store: ${companyInfo.storeNumber}`, companyInfo.email]
            .filter(Boolean).join(" | ")}
        </p>
      </div>
    </div>
  );
};

export default AccountPaymentReceiptModal;
